import React from 'react';
import { motion } from 'framer-motion';
import { usePVData } from '../context/PVDataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit, FiTrash2, FiBarChart, FiMapPin, FiZap, FiFile, FiImage } = FiIcons;

const PlantCard = ({ plant, onEdit, onMonthlyData }) => {
  const { deletePlant, getYearlyData } = usePVData();
  const currentYear = new Date().getFullYear();
  const yearlyProduction = getYearlyData(plant.id, currentYear);

  const handleDelete = () => {
    if (window.confirm(`Möchten Sie die Anlage "${plant.name}" wirklich löschen?`)) {
      deletePlant(plant.id);
    }
  };

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300"
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{plant.name}</h3>
            <div className="flex items-center text-gray-600 text-sm">
              <SafeIcon icon={FiMapPin} className="mr-1" />
              {plant.location}
            </div>
          </div>
          <div className="flex space-x-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onEdit(plant)}
              className="text-primary-600 hover:text-primary-700 p-2 rounded-lg hover:bg-primary-50"
            >
              <SafeIcon icon={FiEdit} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
            >
              <SafeIcon icon={FiTrash2} />
            </motion.button>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex justify-between">
            <span className="text-gray-600">Kennung:</span>
            <span className="font-medium">{plant.identifier}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Kapazität:</span>
            <span className="font-medium">{plant.capacity} kWp</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Strings:</span>
            <span className="font-medium">{plant.stringCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Wechselrichter:</span>
            <span className="font-medium">{plant.inverterCount}</span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-solar-50 to-primary-50 p-4 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Jahresertrag {currentYear}</p>
              <p className="text-xl font-bold text-gray-900">{yearlyProduction.toLocaleString()} kWh</p>
            </div>
            <SafeIcon icon={FiZap} className="text-solar-500 text-2xl" />
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-4">
            {plant.documents && plant.documents.length > 0 && (
              <div className="flex items-center text-gray-600 text-sm">
                <SafeIcon icon={FiFile} className="mr-1" />
                {plant.documents.length} PDF
              </div>
            )}
            {plant.images && plant.images.length > 0 && (
              <div className="flex items-center text-gray-600 text-sm">
                <SafeIcon icon={FiImage} className="mr-1" />
                {plant.images.length} Bilder
              </div>
            )}
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onMonthlyData(plant)}
          className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center space-x-2"
        >
          <SafeIcon icon={FiBarChart} />
          <span>Monatsdaten eingeben</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default PlantCard;